import React, {useEffect, useState} from 'react';
import {
  Box,
  Button,
  CardContent,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Slider,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import EastIcon from '@mui/icons-material/East';
import {SliderValueLabelProps} from '@mui/material/Slider';
import {getAllSubscriptions, getShopifyBuyUrl, getStripePrices} from "../../../../store/actions";
import {useAppDispatch} from "../../../../store/hooks";
import {IError} from "../../../../models/inner-models";
import Swal from "sweetalert2";
import {errorBigIcon} from "../../../../assets/icons/icons";
import {decoratePopUpMessage} from "../../../../utils/popUpTextDecorator";
import {useTranslation} from "react-i18next";
import './style.scss';
import {WhiteCard} from "../../../../components/StyledComponents";

const essentialFeatures = [
  {text: 'AES 256-bit Encryption'},
  {text: 'Two-Factor Authentication'},
  {text: '30 day data retention history'},
  {text: 'Up to 1000 items'},
  {text: 'Automated daily backups'},
  {text: 'Download backups locally'},
  {text: 'Unlimited individual item restores'},
  {text: 'Email notifications'},
  {text: '24 hour email & chat support'},
];

const proFeatures = [
  {text: '90 day data retention history'},
  {text: 'Automated daily backups'},
  {text: 'Backup on demand'},
  {text: 'External storage option'},
  {text: 'Activate/deactivate backups'},
  {text: 'Organisations'},
  {text: 'Telephone support'},
  {text: 'Multiple users & admin control'},
  {text: 'Audit trail'},
];

const plans = [
  {
    name: 'ESSENTIAL',
    chipLabel: 'For individuals',
    description: 'Just want to automatically backup your data every 24 hours.',
    features: essentialFeatures,
  },
  {
    name: 'PRO',
    chipLabel: 'For small to medium organizations',
    description: 'You have more control over your backups.\nIncreased data retention periods, more frequent backups and telephone support.',
    features: proFeatures,
    hasEssentialFeatures: true,
  },
];

interface FeatureProps {
  text: string;
}

interface PlanProps {
  to: number;
  cost: number;
  planId: string
}

const FeatureItem = ({text}: FeatureProps) => (
  <ListItem disableGutters className='shopify-prices__feature-item'>
    <ListItemIcon className='shopify-prices__feature-icon'>
      <CheckIcon className='shopify-prices__check-icon'/>
    </ListItemIcon>
    <ListItemText primary={text} className='shopify-prices__feature-text'/>
  </ListItem>
);

const ShopifyPricesPage = () => {
  const {t: translate} = useTranslation();
  const dispatch = useAppDispatch();
  const [quantity, setQuantity] = useState(10);
  const [curVal, setCurVal] = useState(0);
  const [showAllFeatures, setShowAllFeatures] = useState(false);
  const maxQuantity = 2000;
  const [stripeSub, setStripeSub] = useState({});
  const [loadingData, setLoadingData] = useState<boolean>(true);
  const [waitUrl, setWaitUrl] = useState<boolean>(false);
  const customSteps = [10, 300, 600, 1000, 2000, 2100]; // Define custom step positions
  const labels = [10, 300, 600, 1000, 2000, '2000+'];
  const stepsCount = labels.length;

  const createPriceCalculator = (planData: PlanProps[], isBtn?: boolean) => {
    return (quantity: number): PlanProps => {
      for (const tier of planData) {
        if (tier.to === null) tier.to = 2100;
        if (quantity <= tier.to) return tier
      }
      return {to: 0, cost: 0, planId: ''};
    };
  };

  const calculateEssentialPrice = createPriceCalculator(stripeSub?.essential?.month?.planCost || []);
  const calculateProPrice = createPriceCalculator(stripeSub?.pro_plan?.month?.planCost || []);

  const marks = customSteps.map((label, index) => ({
    value: (index / (stepsCount - 1)) * 100, // Generate values evenly spaced in the range [0, 100]
    label: labels[index]
  }));

  const handleSliderChange = (event: Event, newValue: number) => {
    const stepIndex = Math.round(((newValue as number) / 100) * (stepsCount - 1));
    setCurVal(newValue as number)
    setQuantity(customSteps[stepIndex]);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoadingData(true);
      try {
        await dispatch(getStripePrices()).unwrap().then(res => {
          if (res.result?.shopify)
            setStripeSub({...res.result?.shopify})
        })
      } catch (e) {
        console.log(e);
      } finally {
        setLoadingData(false);
      }
    };

    fetchData();
  }, []);

  const buySubscription = async (planId) => {
    try {
      setWaitUrl(true);
      const resp = await dispatch(getShopifyBuyUrl(planId)).unwrap()
      window.open(resp?.confirmationUrl, '_blank')
      const time = 30000
      const interval = setInterval(() => dispatch(getAllSubscriptions()), time)
      setTimeout(() => {
        setWaitUrl(false)
        clearInterval(interval);
      }, time * 10
      );
    } catch (e) {
      const error = e as IError;
      setWaitUrl(false);
      await Swal.fire({
        imageUrl: errorBigIcon,
        title: translate('notifications.titles.error'),
        text: decoratePopUpMessage(error.error as string),
        confirmButtonText: translate('notifications.choices.close'),
      });
    }
  }

  const ValueLabelComponent = (props: SliderValueLabelProps) => {
    const {children, value} = props;
    const stepIndex = Math.round(((value as number) / 100) * (stepsCount - 1));
    return (
      <Tooltip
        open
        placement="top"
        arrow
        title={stepIndex === 5 ? '2000+ orders to backup' : `Up to ${labels[stepIndex]} orders to backup`}
        slotProps={{
          popper: {
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [0, 0],
                },
              },
            ],
          },
          tooltip: {
            sx: {
              fontSize: '14px',
              backgroundColor: '#3B82F6',
              borderRadius: '8px',
              padding: '10px',
              color: 'white',
            },
          },
          arrow: {
            sx: {
              '&:before': {
                backgroundColor: '#3B82F6',
              },
            },
          },
        }}
      >
        {children}
      </Tooltip>
    );
  };

  return (
    <WhiteCard>
      <div className='shopify-prices'>
        <Typography variant="h4" gutterBottom className='shopify-prices__title'>
          Shopify: Plans and Pricing
        </Typography>
        <Typography variant="body1" color="text.secondary" className='shopify-prices__subtitle'>
          Pay only for what you backup
          <br/>
          <Box component="span" className='shopify-prices__subtitle-strong'>
            Unlimited data recovery
          </Box>
        </Typography>

        <Box className='shopify-prices__slider-box'>
          <Typography variant="body2" className='shopify-prices__slider-label'>
            Choose how many orders to backup:
          </Typography>
          <Box className='shopify-prices__slider-control'>
            <Typography variant="body2" className='shopify-prices__slider-edge'>
              10
            </Typography>
            <Slider
              aria-label="Always visible"
              value={curVal}
              onChange={handleSliderChange}
              valueLabelDisplay="on"
              step={null}
              min={0}
              max={100}
              slots={{valueLabel: ValueLabelComponent}}
              marks={marks}
              className='shopify-prices__slider'
            />
            <Typography variant="body2" className='shopify-prices__slider-edge'>
              {maxQuantity}+
            </Typography>
          </Box>
        </Box>

        {loadingData ?
          <div className="spinner-wrapper">
            <CircularProgress/>
          </div> :
          <Box className='shopify-prices__cards'>
            {plans.map((plan, index) => {
              const price = plan.name === 'ESSENTIAL'
                ? calculateEssentialPrice(quantity).cost
                : calculateProPrice(quantity).cost
              const planId = plan.name === 'ESSENTIAL'
                ? calculateEssentialPrice(quantity).planId
                : calculateProPrice(quantity).planId
              return (
                <Box key={index} className='shopify-prices__card'>
                  <CardContent>
                    <Box className='shopify-prices__card-head'>
                      <Typography variant="h5" className='shopify-prices__plan-title'>
                        {plan.name}
                      </Typography>
                      <Box className='shopify-prices__plan-chip'>{plan.chipLabel}</Box>
                    </Box>
                    <Box className='shopify-prices__price-row'>
                      <Box component="span" className='shopify-prices__price'>
                        {`$${price.toFixed(2)}`}
                      </Box>
                      <Box component="span" className='shopify-prices__price-month'>
                        /month
                      </Box>
                    </Box>
                    <Button
                      variant="contained"
                      fullWidth
                      disabled={waitUrl}
                      className='shopify-prices__buy-btn'
                      onClick={() => {
                        buySubscription(planId)
                      }}
                      endIcon={<EastIcon/>}
                    >
                      Buy Subscription
                    </Button>
                    <Typography
                      variant="body2"
                      className='shopify-prices__trial-note'
                      align="center"
                    >
                      14 days free access. No credit card required
                    </Typography>
                    <Box className='shopify-prices__desc-box'>
                      <Typography variant="body2" className='shopify-prices__desc-text'>
                        {plan.description}
                      </Typography>
                    </Box>
                    <List className='shopify-prices__features-list'>
                      {plan.hasEssentialFeatures && showAllFeatures && (
                        <Typography variant="body2" className='shopify-prices__features-title'>
                          Everything included in ESSENTIAL plus:
                        </Typography>
                      )}
                      {showAllFeatures ? plan.features.map((feature, featureIndex) => (
                          <FeatureItem key={featureIndex} text={feature.text}/>
                        )) :
                        <Button
                          variant="text"
                          fullWidth
                          className='shopify-prices__show-more'
                          onClick={() => setShowAllFeatures(true)}
                        >
                          Show more
                        </Button>}
                    </List>
                  </CardContent>
                </Box>
              )
            })

            }
          </Box>
        }
      </div>
    </WhiteCard>
  );
};

export default ShopifyPricesPage;
